// Module Imports
import { useEffect } from "react"
import { useNavigate } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import { toast } from "react-toastify"

// API Classes Imports
import MetricAPI from "../../../api/metric"
import BlueprintAPI from "../../../api/blueprint"

// Custom Hooks
import { useResponseHandle } from "../../hooks"

// Component Imports
import MetricBlueprintTabs from "../../components/Tabs/MetricBlueprintTabs"
import MetricBlueprintHeader from "../../components/Headers/MetricBlueprintHeader"
import MetricBlueprintTable from "../../components/Table/MetricBlueprintTable"

// Styled Component Imports
import { BMWrapper } from "./Atom"

// Redux Actions
import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"
import {
  setSelectedBlueprintMetrics,
  setSelectedBlueprintMetricsEmpty,
  setSelectedGroups,
  setTabs,
} from "../../redux/slices/metricSlice"

// Helper Methods
import { clearLocalstorage, getTokenLocal } from "../../../helper/utils"

// Creating API Instances
const metricAPI = new MetricAPI()
const blueprintAPI = new BlueprintAPI()

const MetricBlueprint = () => {
  const token: any = getTokenLocal()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  dispatch(setOpenSidebar(false))

  useEffect(() => {
    dispatch(setSelectedBlueprintMetricsEmpty())
  }, [])

  const selectedMetrics = useSelector((state: any) => {
    return state.metric.selectedBlueprintMetrics
  })

  const selectedGroups = useSelector((state: any) => {
    return state.metric.selectedGroups
  })

  const fetchMetricTabs = async () => {
    dispatch(setLoading(true))
    if (token) {
      const result: any = await metricAPI.getMetricTabs(token)
      if (
        result?.data?.status_code != 200 &&
        result?.data?.status_code != 401
      ) {
        toast.error(result?.data?.display_message)
      } else {
        // toast.success(result?.data?.display_message)

        dispatch(setTabs(result?.data?.data))
        dispatch(setLoading(false))
        return result.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  // Metric Blueprint exclusive API methods

  const addNewBucket = async (metricBucketName: any) => {
    dispatch(setLoading(true))
    if (token) {
      const result: any = await blueprintAPI.createNewBlueprintBucket(
        token,
        metricBucketName,
      )
      if (
        result?.data?.status_code != 200 &&
        result?.data?.status_code != 401
      ) {
        toast.error(result?.data?.display_message)
        fetchMetricTabs()
      } else {
        toast.success(result?.data?.display_message)
        fetchMetricTabs()
        dispatch(setLoading(false))
        return result.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  const addNewMetric = async (metricBucketId: any, metricName: any) => {
    dispatch(setLoading(true))
    if (token) {
      const result: any = await blueprintAPI.createNewBlueprintMetric(
        token,
        metricBucketId,
        metricName,
      )
      if (
        result?.data?.status_code != 200 &&
        result?.data?.status_code != 401
      ) {
        toast.error(result?.data?.display_message)
        fetchMetricTabs()
      } else {
        toast.success(result?.data?.display_message)
        fetchMetricTabs()
        dispatch(setLoading(false))
        return result.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  const fetchAllGroups = async () => {
    const response = await metricAPI.getMetricGroups(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      const group: Array<Object> = []
      result?.data?.forEach((g: any) => {
        group.push({ ...g, label: g.group_name })
      })
      return { group: group, ...result }
    } else {
      navigate("/auth")
      return result
    }
  }

  const assignMetricsToGroups = async () => {
    dispatch(setLoading(true))

    const response = await blueprintAPI.assignMetricsToGroup(token, {
      selectedMetrics,
      selectedGroups,
    })

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
    })

    if (result?.isAuthorized) {
      dispatch(setSelectedBlueprintMetrics({ isChecked: null }))
      dispatch(setSelectedBlueprintMetricsEmpty())
      dispatch(setSelectedGroups([]))
    } else {
      navigate("/auth")
    }

    dispatch(setLoading(false))

    return result
  }

  const updateBucketName = async (bucketId: any, newName: string) => {
    const response = await blueprintAPI.updateBucketName(token, {
      bucketId,
      value: newName,
    })

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
      permError: response?.data?.display_message,
    })

    if (result?.isAuthorized) {
      await fetchMetricTabs()
    } else {
      // clearLocalstorage()
      // navigate("/auth")
    }

    return result
  }

  const deleteBucket = async (bucketId: number) => {
    const response = await blueprintAPI.deleteBucket(token, bucketId)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: true,
      permError: response?.data?.display_message,
    })

    if (result?.isAuthorized) {
      await fetchMetricTabs()
    } else {
      // clearLocalstorage()
      // navigate("/auth")
    }

    return result
  }

  useEffect(() => {
    fetchMetricTabs()
  }, [])

  return (
    <BMWrapper>
      <MetricBlueprintHeader
        name="Metric Blueprint Management"
        fetchAllGroups={fetchAllGroups}
        assignMetricsToGroups={assignMetricsToGroups}
      />
      <MetricBlueprintTabs
        fetchMetricTabs={fetchMetricTabs}
        addNewBucket={addNewBucket}
        updateBucketName={updateBucketName}
        deleteBucket={deleteBucket}
      />
      <MetricBlueprintTable
        addNewMetric={addNewMetric}
        fetchMetricTabs={fetchMetricTabs}
      />
    </BMWrapper>
  )
}

export default MetricBlueprint
